"use client";

import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { VideoCallRoom } from "@/features/videocall/components/VideoCallRoom";
import { PostCallReferralPrompt } from "@/components/PostCallReferralPrompt";
import { apiClient } from "@/shared/services/apiClient";
import { supabase } from "@/integrations/supabase/client";

export default function VideoCallPage() {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const location = useLocation() as any;

  const [customerData, setCustomerData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [livekitToken, setLivekitToken] = useState<string | null>(null);
  const [livekitUrl, setLivekitUrl] = useState<string | null>(null);
  const [callEnded, setCallEnded] = useState(false);
  const [callExpired, setCallExpired] = useState(false);
  const [expiryReason, setExpiryReason] = useState<string>("");
  const [showReferralPrompt, setShowReferralPrompt] = useState(false);

  const BASE_URL = import.meta.env.DEV
    ? "http://localhost:3000"
    : import.meta.env.VITE_SIGNALING_SERVER_URL;

  // 🎯 Determine if user is host or guest
  const getIsHost = () => {
    const pathname = window.location.pathname;
    const searchParams = new URLSearchParams(window.location.search);

    // Explicit host paths
    if (pathname.startsWith("/video-call/")) return true;
    if (pathname.startsWith("/call/")) return true; // ✅ FIXED: /call/ is for HOSTS
    if (searchParams.get("host") === "1") return true;

    // Explicit guest paths
    if (pathname.startsWith("/guest/")) return false;
    if (pathname.startsWith("/join/")) return false; // Guests use /join/

    // Check pre-join state
    const prejoinState = location?.state?.prejoin;
    if (prejoinState?.isHost !== undefined) {
      return prejoinState.isHost;
    }

    // Default to guest for safety
    return false;
  };

  const isHost = getIsHost();

  // ✅ FIXED: Detect if running in WebView - skip prejoin redirect
  const isWebView = () => {
    const ua = navigator.userAgent;
    // Check for React Native WebView or our custom identifier
    if (typeof (window as any).ReactNativeWebView !== 'undefined') return true;
    if (ua.includes('WlkthruMobileApp')) return true;
    if ((window as any).__IS_MOBILE_WEBVIEW__ === true) return true;
    return false;
  };

  // 🔁 Redirect to pre-join lobby when arriving directly without prejoin state
  // BUT: Skip for WebView - go straight to call
  // ALSO: Skip for hosts on rejoin - they can go directly to the call
  useEffect(() => {
    if (!roomId) return;

    const hasPrejoin = !!(location?.state && (location.state as any).prejoin);

    // ✅ NEW: WebView bypasses prejoin and goes straight to call
    if (isWebView()) {
      console.log("[VideoCallPage] WebView detected - skipping prejoin, going straight to call");
      setLoading(false); // Allow call to start
      return;
    }

    // ✅ FIX: Allow hosts to rejoin without prejoin state (for rejoins)
    // Hosts are authenticated users, guests still need to go through prejoin for verification
    if (!hasPrejoin && !isHost) {
      console.log("[VideoCallPage] Guest without prejoin state, redirecting to lobby");
      navigate(`/join/${encodeURIComponent(roomId)}`, {
        replace: true,
      });
      return;
    }

    // If host without prejoin, allow through (rejoin case)
    if (!hasPrejoin && isHost) {
      console.log("[VideoCallPage] Host rejoin without prejoin state - allowing direct access");
      setLoading(false);
    }
  }, [roomId, location, navigate, isHost]);

  // 📞 Fetch call session data (customer details) - using backend API
  useEffect(() => {
    const fetchSession = async () => {
      if (!roomId) return;

      try {
        console.log("[VideoCallPage] 🔍 Fetching call_session for room:", roomId);

        // Use backend endpoint instead of direct Supabase call
        const response = await apiClient.get<{ ok: boolean; session: any }>(
          `/session/${roomId}`
        );

        console.log("[VideoCallPage] 📡 Session API response:", {
          ok: response.ok,
          hasSession: !!response.session,
          sessionData: response.session
        });

        if (!response.ok || !response.session) {
          console.warn("[VideoCallPage] ⚠️ No session found - call may not be initialized yet");
          setLoading(false);
          return;
        }

        const data = response.session;

        if (data) {
          console.log("📞 Existing call session found:", data);

          // ✅ NEW: Check if call has expired (30-minute window)
          const now = new Date();
          const expiresAt = data.expires_at ? new Date(data.expires_at) : null;
          const endedAt = data.ended_at ? new Date(data.ended_at) : null;

          // Check if call was explicitly ended
          if (data.status === 'ended' && endedAt) {
            console.warn("⚠️ Call was ended - cannot rejoin");
            setCallExpired(true);
            setExpiryReason("This call has ended");
            setLoading(false);
            return;
          }

          // Check if call has passed 30-minute expiry window
          if (expiresAt && now > expiresAt) {
            console.warn("⚠️ Call has expired (30-minute window passed)");
            setCallExpired(true);
            setExpiryReason("This call has expired. The 30-minute rejoin window has passed.");
            setLoading(false);
            return;
          }

          console.log("✅ Call is still valid - allowing join/rejoin");

          setCustomerData({
            name: data.customer_name,
            phone: data.customer_phone,
            email: data.customer_email,
            project: data.customer_project,
          });
        } else {
          console.log("⚠️ No existing call session found for room:", roomId);
        }
      } catch (err) {
        console.error("❌ Error initializing call session:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchSession();
  }, [roomId]);

  // 🟢 Update call session presence via signaling server
  useEffect(() => {
    if (!roomId || loading) return;

    const updatePresence = async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();

        console.log(
          `[VideoCallPage] Updating presence as ${isHost ? "host" : "guest"}`
        );

        // Get tokens for authentication
        const guestToken = location?.state?.guestToken;

        // Build headers with Authorization for BOTH hosts and guests
        const headers: HeadersInit = { "Content-Type": "application/json" };

        if (isHost) {
          // ✅ SECURITY: Hosts must include Supabase auth token
          // This prevents anyone from adding ?host=1 and joining as host
          const { data: { session } } = await supabase.auth.getSession();

          // Check for mobile WebView injected session (mobile app injects this)
          const mobileSession = (window as any).__MOBILE_SUPABASE_SESSION__;

          if (session?.access_token) {
            headers["Authorization"] = `Bearer ${session.access_token}`;
            console.log("🔑 Host Authorization header added (from Supabase session)");
          } else if (mobileSession?.access_token) {
            // ✅ MOBILE WEBVIEW: Use injected session from mobile app
            headers["Authorization"] = `Bearer ${mobileSession.access_token}`;
            console.log("🔑 Host Authorization header added (from mobile WebView session)");
          } else {
            // Only redirect to login if NOT in WebView (WebView should never hit this)
            if (!isWebView()) {
              console.error("❌ No Supabase session - host cannot join without authentication");
              alert("Please log in to join as host");
              navigate("/login");
              return;
            } else {
              console.warn("⚠️ WebView host without session - proceeding anyway (mobile app should have injected session)");
            }
          }
        } else if (guestToken) {
          headers["Authorization"] = `Bearer ${guestToken}`;
          console.log("🔑 Guest Authorization header added");
        } else if (!isHost) {
          // Guest without token - likely mobile WebView guest bypassing prejoin
          console.log("⚠️ Guest without token - sending join event anyway (WebView flow)");
        }

        const response = await fetch(`${BASE_URL}/api/update-call-session`, {
          method: "POST",
          headers,
          body: JSON.stringify({
            roomId,
            event: isHost ? "host-joined" : "guest-joined",
            participantType: isHost ? "host" : "guest",
            userId: user?.id || null,
          }),
        });

        const data = await response.json();

        if (!data.ok) {
          console.error(
            "[VideoCallPage] Failed to update call session presence:",
            data.error
          );

          // If host verification fails, redirect them
          if (isHost && data.error?.includes("not authorized")) {
            alert("You are not authorized as the host for this call.");
            navigate("/dashboard");
            return;
          }
        } else {
          console.log(`✅ ${isHost ? "Host" : "Guest"} presence updated`);
        }
      } catch (error) {
        console.error(
          "[VideoCallPage] Error updating call session presence:",
          error
        );
      }
    };

    updatePresence();
  }, [roomId, loading, isHost, BASE_URL, navigate]);

  // 🎧 Fetch LiveKit token via backend (for future migration – non-blocking)
  useEffect(() => {
    const fetchToken = async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user || !roomId) return;

        // Get auth token for backend request
        const { data: sessionData } = await supabase.auth.getSession();
        const accessToken = sessionData?.session?.access_token;

        if (!accessToken) {
          console.warn("[VideoCallPage] No access token for LiveKit request");
          return;
        }

        // Call backend endpoint instead of direct Supabase edge function
        const response = await fetch(`${BASE_URL}/api/livekit/token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            room: roomId,
            identity: user.id,
            name: user.user_metadata?.full_name || user.email || user.id,
          }),
        });

        const data = await response.json();

        if (!response.ok || !data.ok) {
          console.warn("[VideoCallPage] LiveKit token fetch error:", data.error);
          return;
        }
        if (data.token) {
          console.log("[VideoCallPage] LiveKit token fetched via backend");
          setLivekitToken(data.token);
          setLivekitUrl(data.url);
        }
      } catch (err) {
        console.warn("[VideoCallPage] LiveKit token fetch failed:", err);
      }
    };

    fetchToken();
  }, [roomId, BASE_URL]);

  const handleEndCall = (callData?: any) => {
    // For guests, show "close this tab" message and referral prompt
    if (!isHost) {
      setCallEnded(true);
      // Show referral prompt after a brief delay
      setTimeout(() => {
        setShowReferralPrompt(true);
      }, 1000);
      return;
    }
    // For hosts, navigate to session summary
    navigate("/session-summary", { state: { callData } });
  };

  // ⏳ Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-black text-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-gray-300">Loading call data...</p>
        </div>
      </div>
    );
  }

  // ✅ NEW: Call expired state
  if (callExpired) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-black text-white">
        <div className="text-center max-w-md px-6">
          <div className="mb-6">
            <svg
              className="w-16 h-16 mx-auto text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-semibold mb-3">Call Unavailable</h1>
          <p className="text-gray-300 mb-6">{expiryReason}</p>
          {isHost ? (
            <button
              onClick={() => navigate("/dashboard")}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition"
            >
              Go to Dashboard
            </button>
          ) : (
            <p className="text-sm text-gray-400">
              Please contact the host if you need to schedule a new call.
            </p>
          )}
        </div>
      </div>
    );
  }

  // ❌ Invalid room
  if (!roomId) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-black text-white">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Invalid Call Link</h1>
          <p className="text-gray-300">
            The call link you used is invalid or has expired.
          </p>
        </div>
      </div>
    );
  }

  // 🔚 Call ended screen for guests
  if (callEnded && !isHost) {
    const prejoinInfo = location?.state?.prejoin;
    const guestName = prejoinInfo?.displayName || "";
    const guestEmail = prejoinInfo?.email || "";
    const guestPhone = prejoinInfo?.phone || "";

    return (
      <>
        <div className="flex items-center justify-center min-h-screen bg-white">
          <div className="max-w-md w-full px-6 py-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h1 className="text-2xl font-semibold text-gray-900 mb-3">
                Call Ended
              </h1>
              <p className="text-gray-600 mb-1">
                Thank you for using WlkThru
              </p>
              <p className="text-sm text-gray-500">
                You can now close this tab
              </p>
            </div>
          </div>
        </div>

        {/* Show PostCallReferralPrompt */}
        {showReferralPrompt && (
          <PostCallReferralPrompt
            onClose={() => setShowReferralPrompt(false)}
            guestName={guestName}
            guestEmail={guestEmail}
            guestPhone={guestPhone}
          />
        )}
      </>
    );
  }

  const prejoinInfo = location?.state?.prejoin;
  const displayName =
    prejoinInfo?.displayName?.trim() || (isHost ? "Host" : "Guest");
  const guestToken = location?.state?.guestToken; // Get guest JWT token from state

  // ✅ FIX: Use prejoin timestamp as key to force VideoCallRoom remount on rejoin
  // This ensures WebRTC services are properly reinitialized when rejoining
  const callKey = prejoinInfo?.timestamp || Date.now();

  return (
    <VideoCallRoom
      key={callKey} // Force remount on each prejoin to fix rejoin issues
      roomId={roomId}
      isHost={isHost}
      onEndCall={handleEndCall}
      customerData={customerData}
      displayName={displayName}
      guestToken={guestToken} // Pass guest token to VideoCallRoom
      // livekitToken={livekitToken} // keep for future if you wire LiveKit in
      // livekitUrl={livekitUrl}
    />
  );
}
